// Beat extraction for the Cinematic Discovery Engine V2.
//
// Turns the SPOKEN narration script into timed documentary beats. Captions must
// stay close to the audio timeline, so we use the same script the listener
// hears and assign timing by spoken-word weight. Captions are complete short
// thoughts - no chopped subtitle fragments.

export type CameraMove =
  | "push-in"
  | "pull-back"
  | "pan-left"
  | "pan-right"
  | "tilt-up"
  | "orbit"
  | "rack-focus"
  | "hold";

export type ParticleMotif =
  | "dust"
  | "embers"
  | "bubbles"
  | "stars"
  | "cells"
  | "snow"
  | "spores";

export type Beat = {
  caption: string;
  // 0..1 timing window within the narration. This keeps overlay text tied to
  // the spoken script instead of fake equal blocks, which caused visible drift.
  startRatio: number;
  endRatio: number;
  camera: CameraMove;
  // 0..1 - cross-fade weight between hero (0) and mid (1) image.
  layerMix: number;
  // Color grading tokens applied to the base image via CSS filters.
  grade: {
    saturate: number;   // 0.7..1.3
    contrast: number;   // 0.9..1.2
    hue: number;        // deg -20..20
    brightness: number; // 0.8..1.1
  };
};

function mergeToLimit(sentences: string[], limit: number): string[] {
  const merged = sentences.slice();
  while (merged.length > limit) {
    let bestIdx = 0;
    let bestLen = Infinity;
    for (let i = 0; i < merged.length - 1; i++) {
      const len = wordCount(merged[i]) + wordCount(merged[i + 1]);
      if (len < bestLen) {
        bestLen = len;
        bestIdx = i;
      }
    }
    merged.splice(bestIdx, 2, `${merged[bestIdx]} ${merged[bestIdx + 1]}`.trim());
  }
  return merged;
}

function alignCaptionsToMaster(displaySentences: string[], masterCount: number, fallback: string): string[] {
  if (masterCount <= 0) return [];
  if (!displaySentences.length) return Array.from({ length: masterCount }, () => fallback);
  if (displaySentences.length === masterCount) return displaySentences;

  return Array.from({ length: masterCount }, (_, i) => {
    const start = Math.floor((i * displaySentences.length) / masterCount);
    const end = Math.max(start + 1, Math.floor(((i + 1) * displaySentences.length) / masterCount));
    return displaySentences.slice(start, Math.min(end, displaySentences.length)).join(" ")
      || displaySentences[Math.min(start, displaySentences.length - 1)]
      || fallback;
  });
}

const CAMERA_SEQUENCE: CameraMove[] = [
  "push-in",
  "pan-right",
  "pull-back",
  "rack-focus",
  "pan-left",
  "tilt-up",
  "orbit",
  "hold",
];

// Split into spoken sentences. IMPORTANT: never drop text. Every spoken word
// consumes audio time, so discarding fragments here shifts the whole caption
// timeline and causes drift later in the article. Short fragments are merged
// into their neighbour instead of filtered out.
function splitSentences(body: string): string[] {
  const raw = body
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/\s+/g, " ")
    // Split Latin and CJK punctuation. Chinese normally has no whitespace
    // after 。！？, so the old English-only boundary collapsed an entire
    // translated story into one caption.
    .split(/(?<=[.!?。！？])(?:\s+|(?=[^\s]))/)
    .map((s) => s.trim())
    .filter(Boolean);

  const out: string[] = [];
  for (const s of raw) {
    const prev = out[out.length - 1];
    if (prev && (wordCount(s) < 4 || wordCount(prev) < 4)) {
      out[out.length - 1] = `${prev} ${s}`.trim();
    } else {
      out.push(s);
    }
  }
  return out;
}

function wordCount(s: string): number {
  // Chinese/Japanese have no spaces: count ~2 CJK characters as one word,
  // otherwise every Chinese sentence looked like a 1-word fragment and the
  // whole story merged into a single giant caption.
  const cjk = (s.match(/[\u3400-\u9fff\uf900-\ufaff]/g) ?? []).length;
  const latin = (s.replace(/[\u3400-\u9fff\uf900-\ufaff]/g, " ").match(/\S+/g) ?? []).length;
  return latin + Math.ceil(cjk / 2);
}

// Estimated spoken duration in seconds. Syllable count tracks TTS pacing far
// better than word count (long words take longer), and punctuation adds the
// real pauses the voice takes. Ratios are normalised afterwards, so only the
// relative accuracy matters - this is what keeps overlay text within ~1s of
// the audio for the whole article.
function spokenWeight(s: string): number {
  const words = s.match(/[A-Za-z0-9']+/g) ?? [];
  let syllables = 0;
  for (const w of words) {
    const groups = w.toLowerCase().match(/[aeiouy]+/g)?.length ?? 1;
    syllables += Math.max(1, /\d/.test(w) ? Math.max(2, w.length) : groups);
  }
  const speech = syllables / 4.2; // ~4.2 syllables per second
  const pauses =
    (s.match(/[.!?]/g)?.length ?? 0) * 0.34 +
    (s.match(/,/g)?.length ?? 0) * 0.16 +
    (s.match(/[;:]/g)?.length ?? 0) * 0.22 +
    (s.match(/[-–]/g)?.length ?? 0) * 0.24 +
    (s.match(/\.\.\./g)?.length ?? 0) * 0.5;
  return Math.max(0.6, speech + pauses);
}


function cleanCaptionText(s: string): string {
  return s
    .replace(/^(Okay so|Okay|So|But|And|Yet|Right|Honestly|Seriously|No joke|Get this|Turns out|I mean|Oh|Hah|Whoa)[,\s--]+/i, "")
    .replace(/["“”]/g, "")
    .replace(/\s+/g, " ")
    .replace(/\s+([,.;:!?])/g, "$1")
    .trim();
}

function scoreCaptionCandidate(s: string): number {
  const t = s.toLowerCase();
  let score = 0;
  if (/\d/.test(s)) score += 2;
  if (/\b(wild|strange|deadly|lethal|giant|tiny|ancient|impossible|secret|hidden|survive|crush|freeze|explode|glow|venom|poison|monster|predator|brain|blood|ocean|space)\b/i.test(s)) score += 3;
  if (/\b(not|never|actually|turns out|because|why|how)\b/i.test(s)) score += 1;
  const wc = wordCount(s);
  if (wc >= 4 && wc <= 12) score += 3;
  if (wc > 16) score -= 3;
  if (/^(it|this|that|they|he|she)\b/.test(t)) score -= 1;
  return score;
}

// Turn a spoken sentence into a short cinematic caption. Prefer a complete
// sentence; otherwise pick a complete clause at punctuation boundaries. Only
// fall back to an ellipsis when there is no natural cut.
function toCaption(sentence: string, fallback: string): string {
  const cleaned = cleanCaptionText(sentence).replace(/[.!?。！？]+$/g, "");
  if (!cleaned) return fallback;
  const fullWords = wordCount(cleaned);
  if (fullWords >= 3 && fullWords <= 13) return cleaned;

  const clauses = cleaned
    .split(/\s*(?:[;:；：，]|\s+-\s+|,\s+)\s*/)
    .map(cleanCaptionText)
    .filter((c) => wordCount(c) >= 3 && wordCount(c) <= 13);
  if (clauses.length) {
    return clauses.sort((a, b) => scoreCaptionCandidate(b) - scoreCaptionCandidate(a))[0];
  }

  if (/[\u3400-\u9fff]/.test(cleaned) && !/\s/.test(cleaned.trim())) {
    return cleaned.length > 26 ? `${cleaned.slice(0, 24)}…` : cleaned;
  }
  const words = cleaned.split(/\s+/);
  const soft = words.slice(0, 11).join(" ").replace(/[,.!?;:]+$/g, "");
  return soft.length >= 3 ? `${soft}…` : fallback;
}

// Guess particle motif from category slug/label so visuals feel article-specific
// without any manual authoring.
export function motifFor(categorySlug?: string | null, categoryLabel?: string | null): ParticleMotif {
  const t = `${categorySlug ?? ""} ${categoryLabel ?? ""}`.toLowerCase();
  if (/ocean|sea|deep|marine|water|fish/.test(t)) return "bubbles";
  if (/space|cosmos|astro|star|galax|univers/.test(t)) return "stars";
  if (/body|human|biolog|anatom|cell|blood|brain|micro/.test(t)) return "cells";
  if (/animal|predator|danger|wild|insect|beast/.test(t)) return "embers";
  if (/plant|fung|forest|nature|tree|jungle/.test(t)) return "spores";
  if (/ice|arctic|snow|polar|cold/.test(t)) return "snow";
  return "dust";
}

export function buildBeats(input: {
  title: string;
  excerpt?: string | null;
  body?: string | null;
  /** The exact script spoken by the audio track. Display copy may be translated. */
  timingBody?: string | null;
}): Beat[] {
  const sentences = splitSentences(input.body ?? "");
  const timingSentences = splitSentences(input.timingBody ?? input.body ?? "");
  const excerpt = (input.excerpt ?? "").trim();
  const minBeats = 8;

  // Use every spoken sentence so the overlay follows the audio. If the script
  // is unusually long we MERGE adjacent lines instead of truncating - dropping
  // sentences would leave spoken audio with no caption and desynchronise
  // everything that follows.
  const MAX_BEATS = 24;
  // The English narration is the timing master. Build its beat boundaries
  // first, then fit every display language into those exact same windows.
  // The previous implementation built the number of windows from the
  // translation, which duplicated/omitted timing weights when sentence counts
  // differed and caused increasingly visible drift through the story.
  const masterSource = timingSentences.length ? timingSentences : sentences;
  const timingPicks = mergeToLimit(masterSource, MAX_BEATS);
  while (timingPicks.length < minBeats) timingPicks.push(excerpt || input.title);

  const displaySource = sentences.length ? sentences : masterSource;
  const displayPicks = mergeToLimit(displaySource, MAX_BEATS);
  const picks = alignCaptionsToMaster(displayPicks, timingPicks.length, excerpt || input.title);
  const timingWeights = timingPicks.map(spokenWeight);
  const weights = timingWeights;
  const totalWeight = weights.reduce((sum, n) => sum + n, 0) || 1;
  let cursor = 0;

  return picks.map((s, i) => {
    const startRatio = cursor / totalWeight;
    cursor += weights[i] ?? 1;
    const endRatio = i === picks.length - 1 ? 1 : cursor / totalWeight;
    const caption = toCaption(s, i === 0 ? input.title : input.title);
    const phase = i / Math.max(1, picks.length - 1);
    return {
      caption,
      startRatio,
      endRatio,
      camera: CAMERA_SEQUENCE[i % CAMERA_SEQUENCE.length],
      // Alternate primary <-> secondary image so beats read as different shots.
      layerMix: i % 2 === 0 ? 0 : 0.72,
      grade: {
        saturate: 0.92 + Math.sin(phase * Math.PI) * 0.18,
        contrast: 1.02 + phase * 0.08,
        hue: (i % 2 === 0 ? -1 : 1) * (4 + (i % 3) * 3),
        brightness: 0.9 + Math.sin(phase * Math.PI) * 0.08,
      },
    };
  });
}
